import { ThemeIcon } from '@mantine/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  ChartBar,
  Checklist,
  LayoutGrid,
  LayoutKanban,
  Rocket,
} from 'tabler-icons-react';

import { useGetUser } from 'api/users/getUser';
import Loader from 'components/Loader';

import styles from './AuthLayout.module.css';

type AuthLayoutProps = {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
};

type TranslateFn = (key: string) => string;

const brandFeatures = [
  {
    icon: LayoutKanban,
    title: 'Multiple views',
    desc: 'Kanban, list, roadmap, calendar, and more.',
  },
  {
    icon: Checklist,
    title: 'Advanced issues',
    desc: 'Labels, components, versions, and epics.',
  },
  {
    icon: ChartBar,
    title: 'Analytics',
    desc: 'Dashboards and reports for data-driven teams.',
  },
  {
    icon: Rocket,
    title: 'Ship faster',
    desc: 'Automation, docs, and resource views.',
  },
];

function getDisplayTitle(title: string, t: TranslateFn): string {
  const map: Record<string, string> = {
    'Sign In': 'auth.signIn',
    'Verify Sign In': 'auth.verifySignIn',
    'Sign Up': 'auth.signUp',
    'Create Account': 'auth.signUp',
    'Request Access': 'auth.requestAccessTitle',
    'Request Received': 'auth.requestReceivedTitle',
    'Request received': 'auth.requestReceivedTitle',
    'Complete Your Profile': 'auth.completeProfile',
    Invite: 'auth.inviteTitle',
  };
  const key = map[title];
  return key ? t(key) : title;
}

function AuthLayout({
  title,
  subtitle,
  children,
}: AuthLayoutProps) {
  const { t } = useTranslation();
  const { data: user, isLoading } = useGetUser();
  const location = useLocation();
  const displayTitle = getDisplayTitle(
    title,
    t as TranslateFn,
  );

  if (isLoading) {
    return <Loader />;
  }

  if (user) {
    const state = location.state as { path: string };
    return (
      <Navigate
        to={state?.path || '/dashboard'}
        replace
      />
    );
  }

  return (
    <div className={styles.wrapper}>
      {/* Left – Branding panel */}
      <div className={styles.brandPanel}>
        <div
          className={`${styles.brandOrb} ${styles.brandOrb1}`}
        />
        <div
          className={`${styles.brandOrb} ${styles.brandOrb2}`}
        />
        <div className={styles.brandContent}>
          <div className={styles.brandLogo}>FlowBoard</div>
          <h2 className={styles.brandHeadline}>
            Ship faster.
            <br />
            Plan smarter.
          </h2>
          <p className={styles.brandSubtext}>
            One platform for issues, docs, and roadmaps.
            {' '}
            Trusted by thousands of teams worldwide.
          </p>
          {brandFeatures.map((feat) => (
            <div key={feat.title} className={styles.brandFeature}>
              <div className={styles.brandFeatureIcon}>
                <feat.icon size={18} />
              </div>
              <div>
                <div className={styles.brandFeatureTitle}>
                  {feat.title}
                </div>
                <div className={styles.brandFeatureText}>
                  {feat.desc}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right – Form panel */}
      <div className={styles.formPanel}>
        <div className={styles.formContainer}>
          <div className={styles.formLogoGroup}>
            <ThemeIcon
              size={38}
              radius="xl"
              variant="gradient"
              gradient={{ from: 'indigo', to: 'violet' }}
            >
              <LayoutGrid size={20} />
            </ThemeIcon>
            <span className={styles.formLogo}>
              FlowBoard
            </span>
          </div>

          <h1 className={styles.formTitle}>
            {displayTitle}
          </h1>
          {subtitle && (
            <p className={styles.formSubtitle}>
              {subtitle}
            </p>
          )}

          <div className={styles.formCard}>
            {children}
          </div>

          <Link to="/" className={styles.backLink}>
            <ArrowLeft size={14} />
            Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}

export default AuthLayout;
